import { useCallback, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { InformationCircleIcon, UserIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';


import analyticsAPI from '@/api/analytics';
import profilesAPI from '@/api/profiles';
import SimpleAnalytics from '@/components/Analytics/SimpleAnalytics';

import useAuth from '@/hooks/useAuth';
import { AnalyticsResponse } from '@/types/IAnalytics';

import Modal from '../Modals/Modal';
import EventsChart from './Charts/Events';
import EventsByGeo from './Charts/EventsByGeo';
import MostActiveProfiles from './Charts/MostActiveProfiles';
import TopReferrers from './Charts/TopReferrers';
import TopSocialLinks from './Charts/TopSocialLinks';
import DateFilter from './DateFilter';
import GroupFilter from './GroupFilter';
import type { Props as GroupFilterProps } from './GroupFilter';
import ProfileFilter from './ProfileFilter';
import type { Props as ProfileFilterProps } from './ProfileFilter';
import './react-datepicker.css';

// Helper functions moved outside component to prevent recreation
const hasDateFormat = (key: string) => {
  return key.match(/^\d{4}-\d{2}-\d{2}$/);
};

const getNextDate = (key: string) => {
  const date = new Date(key);
  date.setDate(date.getDate() + 1);
  return [
    date.getFullYear(),
    ('0' + (date.getMonth() + 1)).slice(-2),
    ('0' + date.getDate()).slice(-2),
  ].join('-');
};

const getNextHour = (key: string) => {
  const date = new Date(key);
  if (date.getHours() === 23) {
    return `${getNextDate(key)} 00:00`;
  }
  const nextHour = (date.getHours() + 1).toString().padStart(2, '0');
  const dateStr = [
    date.getFullYear(),
    ('0' + (date.getMonth() + 1)).slice(-2),
    ('0' + date.getDate()).slice(-2),
  ].join('-');
  return `${dateStr} ${nextHour}:00`;
};

const populateEntries = (entries: { [milestone: string]: number }) => {
  const total = Object.keys(entries).length;
  if (total !== 1) return entries;
  const key = Object.keys(entries)[0];
  const value = entries[key];
  const otherKey = hasDateFormat(key) ? getNextDate(key) : getNextHour(key);
  return { [key]: value, [otherKey]: 0 };
};

const decorateAnalytics = (analytics: AnalyticsResponse): AnalyticsResponse => {
  // Create a copy to avoid mutation
  return {
    ...analytics,
    profile_views: {
      ...analytics.profile_views,
      entries: populateEntries(analytics.profile_views.entries),
    },
    contacts_sent: {
      ...analytics.contacts_sent,
      entries: populateEntries(analytics.contacts_sent.entries),
    },
    contacts_received: {
      ...analytics.contacts_received,
      entries: populateEntries(analytics.contacts_received.entries),
    },
    top_referrers: {
      ...analytics.top_referrers,
      app: {
        ...analytics.top_referrers.app,
        entries: populateEntries(analytics.top_referrers.app.entries),
      },
      card: {
        ...analytics.top_referrers.card,
        entries: populateEntries(analytics.top_referrers.card.entries),
      },
      link: {
        ...analytics.top_referrers.link,
        entries: populateEntries(analytics.top_referrers.link.entries),
      },
    },
  };
};

type AnalyticsDashboardProps = {
  listProfiles: ProfileFilterProps['listProfiles'];
  listGroups: GroupFilterProps['listGroups'];
};

function AnalyticsDashboard({
  listProfiles,
  listGroups,
}: AnalyticsDashboardProps) {
  const { orgID } = useAuth();

  // Initialize with undefined for "All Time" data by default
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [to, setTo] = useState<string | undefined>(undefined);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [profileIds, setProfileIds] = useState<number[]>();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [profileCount, setProfileCount] = useState<number | undefined>(
    undefined,
  );
  const [analytics, setAnalytics] = useState<AnalyticsResponse | undefined>();
  const [error, setError] = useState<string | null>(null);

  // Analytics data loading with improved error handling
  const listAnalytics = useCallback(
    async (
      fromDate?: string,
      toDate?: string,
      groupIdsParam?: number[],
      profileIdsParam?: number[],
    ) => {
      if (orgID === undefined) return;

      try {
        setError(null); // Clear previous errors
        const { data: analyticsData } = await analyticsAPI.listAnalytics(
          orgID,
          fromDate,
          toDate,
          groupIdsParam,
          profileIdsParam,
        );
        const decoratedAnalytics = decorateAnalytics(analyticsData);
        setAnalytics(decoratedAnalytics);
      } catch (err) {
        const errorMessage = 'Failed to load analytics data. Please try again.';
        setError(errorMessage);
        console.error('Analytics fetch error:', err);
      }
    },
    [orgID],
  );

  // Load analytics data on mount and when filters change
  useEffect(() => {
    listAnalytics(from, to, groupIds, profileIds);
  }, [listAnalytics, from, to, groupIds, profileIds]);

  // Memoized profile count function
  const getProfileCount = useCallback(async () => {
    if (orgID === undefined) return { data: { paging: { total_entries: 0 } } };
    const { data: profiles } = await profilesAPI.listProfiles({ orgID });
    setProfileCount(profiles.paging.total_entries);
    return profiles;
  }, [orgID]);

  // Profile count query with proper cache key
  useQuery({
    queryKey: ['listProfiles', orgID],
    queryFn: getProfileCount,
    enabled: orgID !== undefined,
  });

  // Memoized filter handlers
  const handleProfileFilter = useCallback((ids: number[]) => {
    setProfileIds(ids);
  }, []);

  const handleGroupFilter = useCallback((ids: number[]) => {
    setGroupIds(ids);
  }, []);

  const handleDateFilter = useCallback((fromDate?: string, toDate?: string) => {
    setFrom(fromDate);
    setTo(toDate);
  }, []);

  // Show loading skeleton while analytics data is loading
  if (!analytics) {
    return (
      <div className="mb-8 md:mb-0">
        {/* Loading skeleton */}
        <div className="flex justify-end flex-col flex-wrap items-end pt-8 md:pt-0 pb-4 md:pb-8">
          <div className="flex gap-2 flex-row flex-wrap items-center">
            <Skeleton width={109} height={38} />
            <Skeleton width={109} height={38} />
            <Skeleton width={225} height={38} />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 md:p-7 mt-2 md:mt-0">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col flex-1">
              <Skeleton width="40%" height={32} />
              <Skeleton width="75%" height={28} className="mt-2" />
            </div>
            <div className="flex flex-col">
              <Skeleton width={80} height={24} />
              <span className="self-end">
                <Skeleton width={45} height={32} />
              </span>
            </div>
          </div>
          <hr className="h-px mt-4 mb-8 bg-gray-200 border-0" />
          <div className="grid md:grid-cols-3 gap-6 md:gap-10 w-full">
            <Skeleton height="120px" />
            <Skeleton height="120px" />
            <Skeleton height="120px" />
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="mb-8 md:mb-0">
        <div className="bg-white rounded-lg p-4 md:p-7 mt-2 md:mt-0">
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => listAnalytics(from, to, groupIds, profileIds)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Destructure analytics data
  const {
    profile_views: profileViews,
    contacts_sent: contactsSent,
    contacts_received: contactsReceived,
    most_active_profiles: mostActiveProfiles,
    top_referrers: topReferrers,
    top_social_links: topSocialLinks,
    events_by_geo: eventsByGeo,
  } = analytics;

  // Helper to determine if viewing "All Time" data
  const isViewingAllTime = !from || !to;

  return (
    <div className="mb-8 md:mb-0">
      <Modal
        isOpen={showDisclaimer}
        setIsOpen={setShowDisclaimer}
        dialogTitle="Disclaimer"
        dialogDescription="Advanced Analytics only displays data from the 1st of January 2023 onwards."
        cancelButtonText="Close"
        onSuccess={() => {}}
      />
      <div className="flex justify-end flex-col flex-wrap items-end pt-8 md:pt-0 pb-4 md:pb-8">
        <div className="flex gap-2 flex-row-reverse md:flex-row flex-wrap items-center text-center">
          <ProfileFilter
            fetchData={handleProfileFilter}
            listProfiles={listProfiles}
          />
          <GroupFilter fetchData={handleGroupFilter} listGroups={listGroups} />
          <DateFilter fetchData={handleDateFilter} defaultTo90Days={false} />
          {/* Show disclaimer button when viewing "All Time" data */}
          {isViewingAllTime && (
            <button
              onClick={() => setShowDisclaimer(true)}
              type="button"
              className="w-[27px] h-[27px] text-brand-500 cursor-pointer rounded-full font-medium xl:hidden"
            >
              <InformationCircleIcon width={27} height={27} />
            </button>
          )}
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 md:p-7  mt-2 md:mt-0">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col">
            <h2 className="text-gray-900 font-poppins font-medium text-2xl md:text-2xl">
              Domain Analytics
            </h2>
            <span className="text-gray-500 text-sm pt-2">
              Profiles with the most activity (profile views, contacts sent and
              contacts received)
            </span>
          </div>
          <div className="flex flex-col">
            <span className="flex flex-row items-center gap-1 text-gray-500">
              <UserIcon width={20} height={20} strokeWidth={2} />
              Profiles
            </span>
            {profileCount === undefined ? (
              <span className="self-end">
                <Skeleton width={45} height={28} />
              </span>
            ) : (
              <span className="text-right font-medium text-2xl">
                {profileCount}
              </span>
            )}
          </div>
        </div>
        <span className="text-gray-500 mt-2 hidden md:show">
          Data based on profile views, contacts sent and contacts received
        </span>
        <hr className="h-px mt-4 mb-8 bg-gray-200 border-0" />
        <div className="grid md:grid-cols-3 print:grid-cols-3 gap-6 md:gap-10 print:gap-10 w-full">
          <EventsChart
            title="Total profile views"
            total={profileViews.total}
            percent={profileViews.change}
            data={{
              labels: Object.keys(profileViews.entries),
              series: [
                { name: 'Views', data: Object.values(profileViews.entries) },
              ],
            }}
            dateFilter={{ from, to }}
          />
          <EventsChart
            title="Total contacts sent"
            total={contactsSent.total}
            percent={contactsSent.change}
            dateFilter={{ from, to }}
            data={{
              labels: Object.keys(contactsSent.entries),
              series: [
                { name: 'Views', data: Object.values(contactsSent.entries) },
              ],
            }}
          />
          <EventsChart
            title="Total contacts received"
            total={contactsReceived.total}
            percent={contactsReceived.change}
            data={{
              labels: Object.keys(contactsReceived.entries),
              series: [
                {
                  name: 'Views',
                  data: Object.values(contactsReceived.entries),
                },
              ],
            }}
            dateFilter={{ from, to }}
          />
        </div>
      </div>
      {mostActiveProfiles.length > 1 && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg p-4 md:p-7 mt-4 col-span-5 md:col-span-5">
            <MostActiveProfiles
              data={mostActiveProfiles}
              title="Most Active Profiles"
              subtitle="Data based on profile views, contacts sent and contacts received"
              mobileSubtitle="Profiles with the most activity"
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 md:p-7 print:p-7 mt-4 col-span-4 md:col-span-2 print:col-span-2">
          <TopReferrers
            data={topReferrers}
            dateFilter={{ from, to }}
            title="Top Referrers"
            subtitle="Last month website stats"
            mobileSubtitle="Website statistics"
          />
        </div>
        <div className="bg-white rounded-lg p-4 md:p-7 print:p-7 md:mt-4 print:mt-4 col-span-4 md:col-span-2 print:col-span-2">
          <TopSocialLinks
            data={topSocialLinks}
            title="Social Links"
            subtitle="Most clicked social media"
          />
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 md:p-7 mt-4 flex col-span-12 md:col-span-12">
        <EventsByGeo
          data={eventsByGeo}
          title="Tap Geolocation"
          subtitle="Location of profile views, contacts sent and contacts received"
          mobileSubtitle="Location of profile views"
        />
      </div>
      {profileCount && profileCount > 1 && (
        <div className="bg-white rounded-lg p-4 md:p-7 mt-4 flex flex-col col-span-12 md:col-span-12">
          <h2 className="text-gray-900 font-poppins font-medium text-2xl md:text-2xl">
            Profile Analytics
          </h2>
          <h3 className="text-gray-500 pt-2 text-sm hidden md:block">
            Overview of profile analytics. Click {'\u2018'}visit profile
            analytics
            {'\u2018'} to view the analytics of the desired profile with more
            detail.
          </h3>
          <h3 className="text-gray-500 mt-2 md:hidden">
            Overview of individual profile analytics
          </h3>
          <hr className="h-px mt-4 mb-8 bg-gray-200 border-0" />
          <SimpleAnalytics setProfileIds={handleProfileFilter} />
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
